import { useEffect, useRef, type RefObject } from "react";

/**
 * Shared behavior for the layer primitives (Modal, Drawer — UI-LAW
 * §4a). Internal to src/ui; not exported from the package index.
 */

const FOCUSABLE_SELECTOR = [
  "a[href]",
  "button:not([disabled])",
  "input:not([disabled])",
  "select:not([disabled])",
  "textarea:not([disabled])",
  '[tabindex]:not([tabindex="-1"])',
].join(", ");

function focusableDescendants(container: HTMLElement): HTMLElement[] {
  return Array.from(
    container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR),
  ).filter((element) => element.getAttribute("aria-hidden") !== "true");
}

/**
 * Keeps Tab / Shift+Tab cycling inside `containerRef` while `active`. On
 * activation, moves focus to the first focusable descendant (or the
 * container itself, which callers give `tabIndex={-1}`).
 */
export function useFocusTrap(
  containerRef: RefObject<HTMLElement | null>,
  active: boolean,
): void {
  useEffect(() => {
    if (!active) {
      return;
    }
    const container = containerRef.current;
    if (!container) {
      return;
    }
    const initial = focusableDescendants(container)[0] ?? container;
    initial.focus();

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Tab") {
        return;
      }
      const focusable = focusableDescendants(container);
      if (focusable.length === 0) {
        event.preventDefault();
        container.focus();
        return;
      }
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      // noUncheckedIndexedAccess: the length check above guarantees both, but
      // the compiler cannot see that through the index expressions.
      if (first === undefined || last === undefined) {
        return;
      }
      const current = document.activeElement;
      if (event.shiftKey) {
        if (current === first || !container.contains(current)) {
          event.preventDefault();
          last.focus();
        }
        return;
      }
      if (current === last || !container.contains(current)) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener("keydown", handleKeyDown, true);
    return () => {
      document.removeEventListener("keydown", handleKeyDown, true);
    };
  }, [containerRef, active]);
}

/**
 * Captures the focused element when `active` turns on and restores focus to
 * it when the layer closes or unmounts.
 */
export function useFocusRestore(active: boolean): void {
  const previousFocusRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (!active) {
      return;
    }
    const previous = document.activeElement;
    previousFocusRef.current =
      previous instanceof HTMLElement ? previous : null;
    return () => {
      const target = previousFocusRef.current;
      previousFocusRef.current = null;
      if (target !== null && target.isConnected) {
        target.focus();
      }
    };
  }, [active]);
}

/**
 * Locks the page scroll containers while a blocking layer is open. The page
 * behind a Drawer stays visible but must not scroll (UI-LAW §4a). Locks the
 * shell content region when present (the app's real scroll container) and
 * the document element (stories, tests, bare pages).
 */
export function useScrollLock(active: boolean): void {
  useEffect(() => {
    if (!active) {
      return;
    }
    const shellMain = document.querySelector<HTMLElement>(
      ".marker-app-shell__content",
    );
    const documentElement = document.documentElement;
    const previousShellOverflow = shellMain?.style.overflow ?? null;
    const previousDocumentOverflow = documentElement.style.overflow;
    if (shellMain) {
      shellMain.style.overflow = "hidden";
    }
    documentElement.style.overflow = "hidden";
    return () => {
      if (shellMain) {
        shellMain.style.overflow = previousShellOverflow ?? "";
      }
      documentElement.style.overflow = previousDocumentOverflow;
    };
  }, [active]);
}

/**
 * Dismisses the layer on Escape. Matches Popover semantics: ignores events a
 * descendant already handled, and stops propagation so stacked listeners
 * (e.g. the command-palette window handler) do not double-dismiss.
 */
export function useEscapeDismiss(onDismiss: () => void, active: boolean): void {
  const onDismissRef = useRef(onDismiss);
  onDismissRef.current = onDismiss;

  useEffect(() => {
    if (!active) {
      return;
    }
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape" || event.defaultPrevented) {
        return;
      }
      event.stopPropagation();
      onDismissRef.current();
    };
    document.addEventListener("keydown", handleKeyDown, true);
    return () => {
      document.removeEventListener("keydown", handleKeyDown, true);
    };
  }, [active]);
}

export interface AnchoredLayerDismissOptions<
  TAnchor extends HTMLElement,
  TLayer extends HTMLElement,
> {
  active: boolean;
  anchorRef: RefObject<TAnchor | null>;
  layerRef: RefObject<TLayer | null>;
  onDismiss: () => void;
}

/**
 * Dismisses an anchored, non-modal layer when the user clicks outside the
 * trigger+panel pair. Shell chrome popovers use this to match Menu/Popover
 * dismissal without adopting their exact markup.
 */
export function useAnchoredLayerDismiss<
  TAnchor extends HTMLElement,
  TLayer extends HTMLElement,
>({
  active,
  anchorRef,
  layerRef,
  onDismiss,
}: AnchoredLayerDismissOptions<TAnchor, TLayer>): void {
  const onDismissRef = useRef(onDismiss);
  onDismissRef.current = onDismiss;

  useEscapeDismiss(() => {
    onDismissRef.current();
  }, active);

  useEffect(() => {
    if (!active) {
      return;
    }
    const handleMouseDown = (event: MouseEvent) => {
      const target = event.target;
      if (!(target instanceof Node)) {
        return;
      }
      if (layerRef.current?.contains(target)) {
        return;
      }
      if (anchorRef.current?.contains(target)) {
        return;
      }
      onDismissRef.current();
    };
    document.addEventListener("mousedown", handleMouseDown, true);
    return () => {
      document.removeEventListener("mousedown", handleMouseDown, true);
    };
  }, [active, anchorRef, layerRef]);
}
